import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} Oasis: shared in-flight loads, so a second tap never aborts the first. */
  #loading = new Map();
  /** Oasis: true while a tap is being handled; extra taps are ignored. */
  #pending = false;
  /** @type {number | undefined} */
  #closeTimer;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    // Oasis: warm the cache as soon as the visitor shows intent (hover, touch or keyboard focus),
    // so the options sheet opens instantly on tap instead of after a network round trip.
    // Products with the inline size panel do not need it (their options are already on the card).
    if (!this.#panel) {
      this.addEventListener('pointerover', this.#prefetch, { passive: true });
      this.addEventListener('touchstart', this.#prefetch, { passive: true });
      this.addEventListener('focusin', this.#prefetch);
    }

    // Oasis: inline size panel (options listed on the card itself, no modal)
    this.addEventListener('click', this.#onPanelClick);
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerover', this.#prefetch);
    this.removeEventListener('touchstart', this.#prefetch);
    this.removeEventListener('focusin', this.#prefetch);
    this.removeEventListener('click', this.#onPanelClick);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('pointerdown', this.#onOutsidePointer);
    document.removeEventListener('keydown', this.#onPanelKeydown);
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#abortController?.abort();
  }

  /* ------------------------------------------------------------------ */
  /* Oasis: inline size panel                                            */
  /* ------------------------------------------------------------------ */

  get #panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-oas-panel]'));
  }

  get #panelOpen() {
    return this.#panel?.dataset.open === 'true';
  }

  /**
   * Opens or closes the size panel. Products without a panel fall back to the options modal.
   * @param {Event} event
   */
  togglePanel = (event) => {
    if (!this.#panel) return this.handleClick(event);

    event.preventDefault();
    if (this.#panelOpen) {
      this.#closePanel();
    } else {
      this.#openPanel();
    }
  };

  #openPanel() {
    const panel = this.#panel;
    if (!panel) return;

    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    panel.dataset.open = 'true';
    this.toggleAttribute('stay-visible', true);
    this.querySelector('.quick-add__button--choose')?.setAttribute('aria-expanded', 'true');

    document.addEventListener('pointerdown', this.#onOutsidePointer);
    document.addEventListener('keydown', this.#onPanelKeydown);

    const selected = /** @type {HTMLElement | null} */ (panel.querySelector('[data-oas-opt][aria-pressed="true"]'));
    selected?.focus({ preventScroll: true });
  }

  /** @param {boolean} [returnFocus] */
  #closePanel(returnFocus = false) {
    const panel = this.#panel;
    if (!panel) return;

    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    panel.dataset.open = 'false';
    this.toggleAttribute('stay-visible', false);

    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add__button--choose'));
    trigger?.setAttribute('aria-expanded', 'false');
    if (returnFocus) trigger?.focus({ preventScroll: true });

    document.removeEventListener('pointerdown', this.#onOutsidePointer);
    document.removeEventListener('keydown', this.#onPanelKeydown);
  }

  /** @param {PointerEvent} event */
  #onOutsidePointer = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closePanel();
  };

  /** @param {KeyboardEvent} event */
  #onPanelKeydown = (event) => {
    if (event.key === 'Escape') this.#closePanel(true);
  };

  /** @param {Event} event */
  #onPanelClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.#panel) return;

    if (target.closest('[data-oas-close]')) {
      event.preventDefault();
      this.#closePanel(true);
      return;
    }

    const option = /** @type {HTMLButtonElement | null} */ (target.closest('[data-oas-opt]'));
    if (option) {
      event.preventDefault();
      this.#selectOption(option);
    }
  };

  /** @param {HTMLButtonElement} option */
  #selectOption(option) {
    const panel = this.#panel;
    if (!panel || option.disabled) return;

    panel.querySelectorAll('[data-oas-opt]').forEach((el) => {
      el.setAttribute('aria-pressed', el === option ? 'true' : 'false');
    });

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (input) {
      input.value = option.dataset.variantId || '';
      input.disabled = false;
    }

    const selected = panel.querySelector('[data-oas-sel]');
    if (selected) selected.textContent = option.dataset.title || '';

    const price = panel.querySelector('[data-oas-price]');
    if (price) price.textContent = option.dataset.price || '';

    const compare = panel.querySelector('[data-oas-compare]');
    if (compare instanceof HTMLElement) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }

    const addButton = /** @type {import('./product-form').AddToCartComponent | null} */ (
      this.querySelector('add-to-cart-component')
    );
    addButton?.enable();
  }

  /**
   * After a confirmed add the panel stays open just long enough to show "Added", then folds away.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!this.#panelOpen) return;
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.didError) return;

    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#closePanel(), 900);
  };

  #prefetch = () => {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url) || this.#loading.has(url)) return;

    this.#loadGrid(url).catch(() => {});
  };

  /**
   * Loads (and caches) the product grid markup used by the quick add modal.
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  #loadGrid(url) {
    if (!url) return Promise.resolve(null);

    const cached = this.#cachedContent.get(url);
    if (cached) return Promise.resolve(cached);

    const inFlight = this.#loading.get(url);
    if (inFlight) return inFlight;

    const request = this.fetchProductPage(url)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;

        // Cache the cloned element to avoid modifying the original
        const grid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(url, grid);
        return grid;
      })
      .finally(() => {
        this.#loading.delete(url);
      });

    this.#loading.set(url, request);
    return request;
  }

  /**
   * Handles quick add button click (options modal, used by products with more than one option)
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Oasis: one tap at a time. Before, a second tap aborted the first request and opened an empty sheet.
    if (this.#pending) return;
    this.#pending = true;

    const button = this.querySelector('.quick-add__button--choose');
    button?.setAttribute('aria-busy', 'true');

    const currentUrl = this.productPageUrl;

    try {
      const productGrid = await this.#loadGrid(currentUrl);

      if (!this.isConnected) return;

      if (!productGrid) {
        // Could not get the options: send the visitor to the product page instead of a dead button.
        if (currentUrl) window.location.assign(currentUrl);
        return;
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      const ready = await this.updateQuickAddModal(freshContent);

      if (ready === false) {
        if (currentUrl) window.location.assign(currentUrl);
        return;
      }

      this.#openQuickAddModal();
    } catch (error) {
      console.error(error);
      if (this.isConnected && currentUrl) window.location.assign(currentUrl);
    } finally {
      this.#pending = false;
      button?.removeAttribute('aria-busy');
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @returns {Promise<boolean>} false when the modal content could not be prepared
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return false;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return false;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return false;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    return true;
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
